import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Thermometer,
  Clock,
  Zap,
  RefreshCw,
  AlertCircle,
  Bell,
  BellOff,
  Eye,
  X,
  Calendar,
  Building2,
  Snowflake,
} from "lucide-react";
import { FaTrash } from "react-icons/fa";   

import { getAlertes, deleteAlerte } from "../../services/alertes";
import type { Alerte } from "../../types/alerte";

// Helpers
function formatDate(date: string, heure: string) {
  const d = new Date(`${date}T${heure}`);
  return (
    d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) +
    " à " +
    d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
  );
}

function tempsEcoule(date: string, heure: string) {
  const d = new Date(`${date}T${heure}`);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "À l'instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `Il y a ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  return `Il y a ${diffD} j`;
}

function getNatureConfig(nature: string) {
  const n = nature.toLowerCase();

  if (n.includes("paramètres hors seuil")) {
    return { icon: Thermometer, couleur: "red", label: "Paramètres hors seuil" };
  }
  if (n.includes("retard")) {
    return { icon: Clock, couleur: "orange", label: "Retard de relève" };
  }
  if (n.includes("maintenance")) {
    return { icon: RefreshCw, couleur: "blue", label: "Maintenance" };
  }
  if (n.includes("compresseur") || n.includes("ventilateur")) {
    return { icon: Zap, couleur: "purple", label: "Équipement" };
  }
  return { icon: AlertCircle, couleur: "gray", label: "Autre" };
}

// Carte alerte
function CarteAlerte({
  alerte,
  onVoir,
  onSupprimer,
}: {
  alerte: Alerte;
  onVoir: (a: Alerte) => void;
  onSupprimer: (id: string) => void;
}) {
  const { couleur, icon: NatureIcon } = getNatureConfig(alerte.nature);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`carte-alerte border-l-4 border-${couleur}-500`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3 mb-3 justify-between">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg bg-${couleur}-100`}>
              <NatureIcon className={`w-4 h-4 text-${couleur}-600`} />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold">{alerte.nature}</h4>
              <p className="text-xs text-gray-500">
                {tempsEcoule(alerte.date_alerte, alerte.heure_alerte)}
              </p>
            </div>
          </div>
          {/* Bouton suppression avec FaTrash */}
          <button
            onClick={() => onSupprimer(alerte.id)}
            className="text-red-600 hover:text-red-800"
            title="Supprimer l'alerte"
          >
            <FaTrash className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-4 text-xs text-gray-600 mb-2">
          <span className="flex items-center gap-1">
            <Building2 className="w-3 h-3" /> {alerte.entrepot.nom}
          </span>
          <span className="flex items-center gap-1">
            <Snowflake className="w-3 h-3" /> {alerte.chambre_froide.nom}
          </span>
        </div>

        {alerte.parametres_concernes && (
          <p className="text-xs text-gray-700 bg-gray-50 rounded-lg px-3 py-2 mt-2 border">
            {alerte.parametres_concernes}
          </p>
        )}

        <div className="flex items-center justify-between mt-3 pt-3 border-t">
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {formatDate(alerte.date_alerte, alerte.heure_alerte)}
          </span>
          <button
            onClick={() => onVoir(alerte)}
            className="text-xs text-blue-600 flex items-center gap-1 hover:underline"
          >
            <Eye className="w-3 h-3" /> Détails
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// Module principal
export default function Alertes() {
  const [alertes, setAlertes] = useState<Alerte[]>([]);
  const [alerteSelectionnee, setAlerteSelectionnee] = useState<Alerte | null>(null);
  const [recherche, setRecherche] = useState("");

  useEffect(() => {
    const safeSetArray = <T,>(setter: React.Dispatch<React.SetStateAction<T[]>>) => (data: unknown) => {
      if (Array.isArray(data)) setter(data as T[]);
      else setter([]);
    };

    getAlertes().then(safeSetArray<Alerte>(setAlertes));
  }, []);


  const handleSupprimer = async (id: string) => {
    await deleteAlerte(id);
    setAlertes((prev) => prev.filter((a) => a.id !== id));
  };

  const alertesFiltrees = useMemo(() => {
    return alertes.filter(
      (a) =>
        a.nature.toLowerCase().includes(recherche.toLowerCase()) ||
        a.entrepot.nom.toLowerCase().includes(recherche.toLowerCase()) ||
        a.chambre_froide.nom.toLowerCase().includes(recherche.toLowerCase()) ||
        (a.parametres_concernes?.toLowerCase().includes(recherche.toLowerCase()) ?? false)
    );
  }, [alertes, recherche]);

  return (
    <div className="alertes-module space-y-6">
      <div className="flex items-center gap-3">
        <Bell className="w-5 h-5 text-blue-600" />
      </div>

      <input
        type="text"
        placeholder="Rechercher une alerte..."
        value={recherche}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRecherche(e.target.value)}
        className="search-input"
      />

      {alertesFiltrees.length === 0 ? (
        <div className="no-alertes">
          <BellOff className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">Aucune alerte correspondant à vos critères</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {alertesFiltrees.map((alerte) => (
            <CarteAlerte
              key={alerte.id}
              alerte={alerte}
              onVoir={setAlerteSelectionnee}
              onSupprimer={handleSupprimer}
            />
          ))}
        </div>
      )}

            <AnimatePresence>
        {alerteSelectionnee && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={() => setAlerteSelectionnee(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => setAlerteSelectionnee(null)} className="close-btn">
                <X className="w-5 h-5" />
              </button>
              <h2 className="text-lg font-semibold">{alerteSelectionnee.nature}</h2>
              <p className="text-sm text-gray-500">
                {formatDate(
                  alerteSelectionnee.date_alerte,
                  alerteSelectionnee.heure_alerte
                )}
              </p>
              {alerteSelectionnee.parametres_concernes && (
                <p className="mt-4 text-sm text-red-600">
                  {alerteSelectionnee.parametres_concernes}
                </p>
              )}
              {/* Tu peux enrichir ici avec entrepôt, chambre, groupe frigorifique */}
              <div className="mt-4 text-xs text-gray-600 space-y-1">
                <p>
                  <Building2 className="inline w-3 h-3 mr-1" />
                  {alerteSelectionnee.entrepot.nom}
                </p>
                <p>
                  <Snowflake className="inline w-3 h-3 mr-1" />
                  {alerteSelectionnee.chambre_froide.nom}
                </p>
                {alerteSelectionnee.groupe_frigorifique && (
                  <p>
                    Groupe : {alerteSelectionnee.groupe_frigorifique.nom}
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
